/**
 * Shared helpers for stashing/restoring the in-app URL the user was on before
 * being redirected to the portal login, so they are returned to that page
 * after a login round-trip instead of being dropped on the home route
 * (bug 7719533).
 *
 * The stash lives in sessionStorage. Browsers COPY sessionStorage into a
 * duplicated tab, so a bare URL would be restored into the wrong tab. To
 * prevent that, the stash is tagged with a per-tab id that is generated once
 * per window and held in memory (never persisted), so a duplicated tab gets a
 * fresh id that will not match the copied value and the stale URL is ignored.
 */

/** sessionStorage key holding the stashed return URL (JSON `StashedReturnUrl`). */
export const RETURN_URL_STORAGE_KEY = 'gain.returnUrl';

interface StashedReturnUrl {
  url: string;
  tabId: string;
}

/**
 * Per-tab id, generated once per window and kept only in memory. A duplicated
 * tab does not copy module state, so it gets a new id; the sessionStorage value
 * it inherited from the source tab therefore will not match.
 */
const currentTabId: string = generateTabId();

function generateTabId(): string {
  const cryptoObj = globalThis.crypto;
  if (cryptoObj && typeof cryptoObj.randomUUID === 'function') {
    return cryptoObj.randomUUID();
  }
  // Fallback for environments without crypto.randomUUID.
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/** Remember the current in-app URL so we can return to it after login. */
export function stashReturnUrl(url: string): void {
  // Don't bother stashing the home route; there's nothing to restore.
  if (url && url !== '/' && url !== '/clients') {
    const value: StashedReturnUrl = { url, tabId: currentTabId };
    sessionStorage.setItem(RETURN_URL_STORAGE_KEY, JSON.stringify(value));
  }
}

/**
 * Read (and clear) a stashed return URL, but only if it was stashed by THIS
 * tab. A value inherited from a duplicated tab carries a foreign tabId and is
 * discarded. Returns null when there is nothing valid to restore.
 */
export function takeReturnUrl(): string | null {
  const raw = sessionStorage.getItem(RETURN_URL_STORAGE_KEY);
  sessionStorage.removeItem(RETURN_URL_STORAGE_KEY);
  if (!raw) {
    return null;
  }

  let parsed: Partial<StashedReturnUrl>;
  try {
    parsed = JSON.parse(raw) as Partial<StashedReturnUrl>;
  } catch {
    // Legacy or corrupt value (e.g. a bare URL from a previous version).
    return null;
  }

  if (parsed.tabId !== currentTabId) {
    // Foreign tab (duplicated) or concurrent reload: ignore it.
    return null;
  }

  return parsed.url ?? null;
}

/**
 * Set once a full-page redirect to the portal login has been triggered. Held
 * only in memory (the page is about to be torn down). Lets the in-app
 * navigation-error recovery skip a redundant `/unauthorized` navigation that
 * would otherwise compete with the full-page redirect already in flight.
 */
let portalLoginRedirectInFlight = false;

/** Record that a full-page portal login redirect has been triggered. */
export function markPortalLoginRedirectInFlight(): void {
  portalLoginRedirectInFlight = true;
}

/** True once a full-page portal login redirect has been triggered this page load. */
export function isPortalLoginRedirectInFlight(): boolean {
  return portalLoginRedirectInFlight;
}

/** Test-only: reset the redirect-in-flight flag so specs don't leak state. */
export function resetPortalLoginRedirectInFlightForTest(): void {
  portalLoginRedirectInFlight = false;
}
